import { tagList } from "@ir/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/layout/PageHeader";
import { TagsTable } from "@/features/tags/TagsTable";
import { ApiError, apiFetch } from "@/lib/api";
import { authClient } from "@/lib/authClient";

export const Route = createFileRoute("/_auth/admin/tags")({
  async beforeLoad() {
    const { data } = await authClient.getSession();
    if (data?.user.role !== "ir_admin") redirect({ to: "/insights", throw: true });
  },
  component: AdminTagsPage,
});

function AdminTagsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const tags = useQuery({
    queryKey: ["tags"],
    queryFn: () => apiFetch("/api/tags", { schema: tagList }),
    // Sin reintentos: un fallo aquí se enseña, no se disimula con tres
    // intentos que solo retrasan el mensaje.
    retry: false,
  });

  // Renombrar y fusionar cambian las etiquetas de los insights, así que
  // también se invalida el catálogo, no solo la lista de tags. Las tres
  // operaciones lo comparten a propósito: borrar una etiqueta en uso deja los
  // insights que la llevaban con una etiqueta menos.
  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: ["tags"] });
    await queryClient.invalidateQueries({ queryKey: ["studies"] });
  };

  // Sin `onError`: el error viaja hasta el diálogo, que lo pinta sobre el
  // control y se queda abierto para reintentar. Un toast además del mensaje
  // en línea diría lo mismo dos veces.
  const rename = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      apiFetch(`/api/tags/${id}`, { method: "PATCH", body: JSON.stringify({ name }) }),
    onSuccess: refresh,
  });

  const merge = useMutation({
    mutationFn: ({ id, intoId }: { id: string; intoId: string }) =>
      apiFetch(`/api/tags/${id}/merge`, { method: "POST", body: JSON.stringify({ intoId }) }),
    onSuccess: refresh,
  });

  const remove = useMutation({
    mutationFn: (id: string) => apiFetch(`/api/tags/${id}`, { method: "DELETE" }),
    onSuccess: refresh,
  });

  return (
    <>
      <PageHeader title={t("tags.title")} subtitle={t("tags.subtitle")} />

      <div className="px-8 py-6">
        <div className="overflow-x-auto rounded-[3px] border border-line bg-card">
          {tags.isError && (
            <p className="p-4 text-nota text-warn">
              {tags.error instanceof ApiError ? tags.error.message : t("common.error")}
            </p>
          )}

          {tags.isPending && <p className="p-4 text-nota text-ink3">{t("common.loading")}</p>}

          {tags.data && tags.data.items.length === 0 && (
            <p className="p-4 text-nota text-ink3">{t("tags.empty")}</p>
          )}

          {tags.data && tags.data.items.length > 0 && (
            <TagsTable
              tags={tags.data.items}
              onRename={async (id, name) => {
                await rename.mutateAsync({ id, name });
              }}
              onMerge={async (id, intoId) => {
                await merge.mutateAsync({ id, intoId });
              }}
              onDelete={async (id) => {
                await remove.mutateAsync(id);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}
